/**
 * The environment agents Orgus starts run in (ADR 0093): the machine owner's own user
 * environment, read from its source (the Windows registry, or the login shell elsewhere), with a
 * short documented list of differences per launch, and a parity check that proves it.
 *
 *   agent -> hub  { ext: 'agent-environment', type: 'report',  data: AgentEnvironmentReport }
 *   hub -> agent  { ext: 'agent-environment', type: 'refresh', data: {} }  read the environment and check again now
 *
 * The parity check resolves ENV_PARITY_TOOLS the way each launch's own shell does (PowerShell's
 * Get-Command for Codex and ACP agents, `where` for cmd, Git Bash's `command -v` for Claude Code on
 * Windows, a PATH walk elsewhere), once in the user's environment and once in each launch's, and
 * reports the differences: names and paths, never a value of any other variable.
 *
 * state.scheduler.environment lists each machine's latest report (module 'agent-environment');
 * Capacity, Rules, Coding agents shows one line per agent (environmentParityLine). Pure and
 * browser-safe.
 */
import type { Id, Ts } from '../index.ts';

export const AGENT_ENVIRONMENT_EXT = 'agent-environment';
export const AGENT_ENVIRONMENT_TYPE = 'report';
export const AGENT_ENVIRONMENT_REFRESH_TYPE = 'refresh';

/** The tools the parity check resolves, in report order. */
export const ENV_PARITY_TOOLS = ['git', 'node', 'pnpm', 'npm', 'python', 'codex', 'gemini', 'claude'] as const;
export type EnvParityTool = (typeof ENV_PARITY_TOOLS)[number];

/**
 * What a launch is, for its environment (environmentForLaunch in agents/agent-ts/src/launch-env.ts):
 *   claude-code  Claude Code sessions (headless, terminal windows, terminals) and the /usage probe
 *   codex        Codex sessions (headless and terminal windows), plus the Codex provider variables
 *   ai-job       Orgus's own AI jobs on any harness (Claude Code, Codex, Gemini CLI)
 *   acp          ACP agents' sessions (Gemini CLI and the registry's agents)
 *   shell        an admin's shell terminal
 *   job          a job.run command and a worktree's setup command
 */
export const ENV_LAUNCH_KINDS = ['claude-code', 'codex', 'ai-job', 'acp', 'shell', 'job'] as const;
export type EnvLaunchKind = (typeof ENV_LAUNCH_KINDS)[number];

/**
 * How much of the user's environment a launch gets. 'allow-list': only the launch allow-list's
 * names (system basics, proxies, toolchain homes, the harnesses' own settings; launch-env.ts
 * ALLOW). 'full': all of it. Both drop Orgus's own variables and the host session's identity.
 */
export const ENV_LAUNCH_POLICY: Readonly<Record<EnvLaunchKind, 'allow-list' | 'full'>> = {
  'claude-code': 'allow-list',
  codex: 'allow-list',
  'ai-job': 'allow-list',
  acp: 'full',
  shell: 'full',
  job: 'full',
};

export const ENV_LAUNCH_LABEL: Readonly<Record<EnvLaunchKind, string>> = {
  'claude-code': 'Claude Code sessions',
  codex: 'Codex sessions',
  'ai-job': 'AI jobs',
  acp: 'ACP agent sessions',
  shell: 'shells',
  job: 'commands',
};

/** The launch class an agent kind's sessions use: Claude Code, Codex, or any ACP agent. */
export function envLaunchKindOf(agentKind: string): EnvLaunchKind {
  return agentKind === 'claude-code' ? 'claude-code' : agentKind === 'codex' ? 'codex' : 'acp';
}

/**
 * How a name becomes a program: PowerShell's Get-Command (the first command it would run),
 * where.exe filtered to PATHEXT (what cmd runs), Git Bash's `command -v` (a login shell), or a
 * PATH walk for executable files (execvp, macOS and Linux).
 */
export type EnvResolver = 'powershell' | 'cmd' | 'bash' | 'path';
/** The PowerShell resolver, by name: agent code names it through this, so no source spells the program (R-EN-3 gate). */
export const POWERSHELL_RESOLVER: EnvResolver = 'powershell';
export const ENV_RESOLVER_LABEL: Readonly<Record<EnvResolver, string>> = { powershell: 'PowerShell', cmd: 'cmd', bash: 'Git Bash', path: 'PATH' };

/** Where the user's environment came from. */
export type UserEnvironmentSource = 'registry' | 'login-shell';

/**
 * The user environment's state on a machine: 'read' (launches start from it), 'fallback' (it
 * could not be read or failed validation: launches start from the agent's own environment, with
 * the Windows PATH order under Git Bash) or 'off' (ORGUS_USER_ENV=off, or a test run).
 */
export interface UserEnvironmentStatus {
  state: 'read' | 'fallback' | 'off';
  source: UserEnvironmentSource | null;
  /** Why it is not 'read'. Never a variable's value. */
  reason?: string;
  readAt?: Ts;
  /** How many variables it has. */
  names: number;
}

/** One tool as one resolver found it. */
export interface ToolResolution {
  tool: EnvParityTool;
  /** The program's path, or null when nothing of that name is found. */
  path: string | null;
  /** The first line of `<path> --version`, when it was read (never for a file Windows cannot run). */
  version?: string | null;
  /** Windows: the file has no extension, so Windows asks which app should open it. */
  noExtension?: boolean;
}

/** Every tool through one resolver in one environment. */
export interface EnvResolutionSet {
  resolver: EnvResolver;
  tools: ToolResolution[];
  /** The resolver could not run (no PowerShell, no Git Bash, a timeout). */
  error?: string;
}

/** One tool that resolves differently in a launch than for the user. */
export interface EnvParityDifference {
  tool: EnvParityTool;
  resolver: EnvResolver;
  launch: string | null;
  user: string | null;
  launchNoExtension?: boolean;
  userNoExtension?: boolean;
}

/** One launch class compared with the user's environment. */
export interface EnvParityLaunch {
  launch: EnvLaunchKind;
  resolvers: EnvResolver[];
  status: 'match' | 'differs' | 'unknown';
  differences: EnvParityDifference[];
  /** The launch's own resolutions, one set per resolver (versions included when read). */
  sets?: EnvResolutionSet[];
  error?: string;
}

/** A machine's latest environment report. */
export interface AgentEnvironmentReport {
  machineId: Id;
  at: Ts;
  platform: string;
  environment: UserEnvironmentStatus;
  /** The user's own resolutions, one set per resolver used. */
  user: EnvResolutionSet[];
  launches: EnvParityLaunch[];
  /** 'differs' when any launch differs, 'unknown' when none could be compared. */
  status: 'match' | 'differs' | 'unknown';
  tookMs?: number;
}

/** What module 'agent-environment' offers: ctx.ext<AgentEnvironmentApi>('agent-environment'). */
export interface AgentEnvironmentApi {
  list(): AgentEnvironmentReport[];
  get(machineId: Id): AgentEnvironmentReport | null;
}

const where = (p: string | null, noExt: boolean | undefined): string => (p ? `${p}${noExt ? ' (no extension)' : ''}` : 'nothing');

/**
 * One difference in words: "pnpm resolves to C:\Users\me\bin\pnpm (no extension) in Codex
 * sessions, to C:\Users\me\AppData\Roaming\npm\pnpm.cmd for you".
 */
export function parityDifferenceText(d: EnvParityDifference, launch: EnvLaunchKind): string {
  const label = ENV_LAUNCH_LABEL[launch];
  if (!d.launch) return `${d.tool} is not found in ${label}, it resolves to ${where(d.user, d.userNoExtension)} for you`;
  if (!d.user) return `${d.tool} resolves to ${where(d.launch, d.launchNoExtension)} in ${label}, and is not found for you`;
  return `${d.tool} resolves to ${where(d.launch, d.launchNoExtension)} in ${label}, to ${where(d.user, d.userNoExtension)} for you`;
}

/** The one line under an agent in Capacity, Rules, Coding agents. */
export interface EnvironmentParityLine {
  tone: 'ok' | 'attention';
  text: string;
  /** Every difference in full, for a tooltip. */
  detail: string | null;
}

/**
 * The line for one agent kind across the machines' reports: attention when any machine's launch
 * of that kind differs or could not read the user environment, ok when every report compared and
 * matched, null when no machine has reported. `machineName` names a machine when there are several.
 */
export function environmentParityLine(reports: readonly AgentEnvironmentReport[], agentKind: string, machineName: (id: Id) => string | null = () => null): EnvironmentParityLine | null {
  const launch = envLaunchKindOf(agentKind);
  const on = (r: AgentEnvironmentReport) => {
    const n = reports.length > 1 ? machineName(r.machineId) : null;
    return n ? ` on ${n}` : '';
  };
  const fallback = reports.find((r) => r.environment.state === 'fallback');
  const differing = reports.flatMap((r) => r.launches.filter((l) => l.launch === launch && l.status === 'differs').map((l) => ({ r, l })));
  if (differing.length) {
    const tools = [...new Set(differing.flatMap(({ l }) => l.differences.map((d) => d.tool)))];
    const first = differing[0]!;
    const detail = differing.flatMap(({ r, l }) => l.differences.map((d) => `${parityDifferenceText(d, launch)}${on(r)}`)).join('. ');
    const names = tools.length > 2 ? `${tools.slice(0, 2).join(', ')} and ${tools.length - 2} more` : tools.join(' and ');
    return { tone: 'attention', text: `${names} ${tools.length === 1 ? 'resolves' : 'resolve'} differently than in your terminal${on(first.r)}`, detail: `${detail}.` };
  }
  if (fallback) return { tone: 'attention', text: `Your environment could not be read${on(fallback)}, so sessions use the agent's`, detail: fallback.environment.reason ?? null };
  const compared = reports.filter((r) => r.launches.some((l) => l.launch === launch && l.status === 'match'));
  if (!compared.length) return null;
  return { tone: 'ok', text: 'Same tools as your terminal', detail: null };
}

// ── Validation (the hub keeps only what fits) ────────────────────────────────

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max: number): string | null => (typeof v === 'string' && v.length > 0 ? v.slice(0, max) : null);
const TOOLS = new Set<string>(ENV_PARITY_TOOLS);
const LAUNCHES = new Set<string>(ENV_LAUNCH_KINDS);
const RESOLVERS = new Set<string>(['powershell', 'cmd', 'bash', 'path']);
const STATUSES = new Set<string>(['match', 'differs', 'unknown']);
const PATH_MAX = 1024;

function cleanResolution(v: unknown): ToolResolution | null {
  if (!isObj(v) || !TOOLS.has(v.tool as string)) return null;
  const version = str(v.version, 120);
  return { tool: v.tool as EnvParityTool, path: str(v.path, PATH_MAX), ...(version ? { version } : {}), ...(v.noExtension === true ? { noExtension: true } : {}) };
}

function cleanSet(v: unknown): EnvResolutionSet | null {
  if (!isObj(v) || !RESOLVERS.has(v.resolver as string)) return null;
  const tools = (Array.isArray(v.tools) ? v.tools.slice(0, ENV_PARITY_TOOLS.length) : []).map(cleanResolution).filter((x): x is ToolResolution => !!x);
  const error = str(v.error, 300);
  return { resolver: v.resolver as EnvResolver, tools, ...(error ? { error } : {}) };
}

function cleanDifference(v: unknown): EnvParityDifference | null {
  if (!isObj(v) || !TOOLS.has(v.tool as string) || !RESOLVERS.has(v.resolver as string)) return null;
  return {
    tool: v.tool as EnvParityTool,
    resolver: v.resolver as EnvResolver,
    launch: str(v.launch, PATH_MAX),
    user: str(v.user, PATH_MAX),
    ...(v.launchNoExtension === true ? { launchNoExtension: true } : {}),
    ...(v.userNoExtension === true ? { userNoExtension: true } : {}),
  };
}

function cleanLaunch(v: unknown): EnvParityLaunch | null {
  if (!isObj(v) || !LAUNCHES.has(v.launch as string)) return null;
  const resolvers = (Array.isArray(v.resolvers) ? v.resolvers.slice(0, 4) : []).filter((r): r is EnvResolver => RESOLVERS.has(r as string));
  const differences = (Array.isArray(v.differences) ? v.differences.slice(0, 32) : []).map(cleanDifference).filter((x): x is EnvParityDifference => !!x);
  const error = str(v.error, 300);
  const status = STATUSES.has(v.status as string) ? (v.status as EnvParityLaunch['status']) : 'unknown';
  const sets = (Array.isArray(v.sets) ? v.sets.slice(0, 4) : []).map(cleanSet).filter((x): x is EnvResolutionSet => !!x);
  return { launch: v.launch as EnvLaunchKind, resolvers, status: status === 'match' && differences.length ? 'differs' : status, differences, ...(sets.length ? { sets } : {}), ...(error ? { error } : {}) };
}

/** An agent's report as the hub keeps it (bounded, typed), or null when it is not one. */
export function cleanAgentEnvironmentReport(v: unknown, machineId: Id, now: Ts): AgentEnvironmentReport | null {
  if (!isObj(v) || !isObj(v.environment)) return null;
  const e = v.environment;
  const state = e.state === 'read' || e.state === 'fallback' || e.state === 'off' ? e.state : null;
  if (!state) return null;
  const source = e.source === 'registry' || e.source === 'login-shell' ? e.source : null;
  const reason = str(e.reason, 300);
  const readAt = typeof e.readAt === 'number' && Number.isFinite(e.readAt) ? e.readAt : null;
  const environment: UserEnvironmentStatus = { state, source, ...(reason ? { reason } : {}), ...(readAt ? { readAt } : {}), names: typeof e.names === 'number' && Number.isFinite(e.names) ? Math.max(0, Math.floor(e.names)) : 0 };
  const user = (Array.isArray(v.user) ? v.user.slice(0, 4) : []).map(cleanSet).filter((x): x is EnvResolutionSet => !!x);
  const launches = (Array.isArray(v.launches) ? v.launches.slice(0, ENV_LAUNCH_KINDS.length) : []).map(cleanLaunch).filter((x): x is EnvParityLaunch => !!x);
  const tookMs = typeof v.tookMs === 'number' && Number.isFinite(v.tookMs) ? Math.max(0, Math.round(v.tookMs)) : null;
  return { machineId, at: now, platform: str(v.platform, 20) ?? 'unknown', environment, user, launches, status: parityStatus(launches), ...(tookMs !== null ? { tookMs } : {}) };
}

/** The report's status from its launches: any difference wins, then any match. */
export function parityStatus(launches: readonly Pick<EnvParityLaunch, 'status'>[]): AgentEnvironmentReport['status'] {
  if (launches.some((l) => l.status === 'differs')) return 'differs';
  return launches.some((l) => l.status === 'match') ? 'match' : 'unknown';
}

/**
 * The differences between the user's resolutions and a launch's, per resolver and tool. Paths
 * compare case-insensitively on Windows. A tool missing from either set is not compared.
 */
export function parityDifferences(user: readonly EnvResolutionSet[], launch: readonly EnvResolutionSet[], platform: string): EnvParityDifference[] {
  const same = (a: string | null, b: string | null) => (platform === 'win32' ? (a ?? '').toLowerCase() === (b ?? '').toLowerCase() : a === b);
  const out: EnvParityDifference[] = [];
  for (const l of launch) {
    const u = user.find((s) => s.resolver === l.resolver);
    if (!u || u.error || l.error) continue;
    for (const lt of l.tools) {
      const ut = u.tools.find((t) => t.tool === lt.tool);
      if (!ut || (same(ut.path, lt.path) && !!ut.noExtension === !!lt.noExtension)) continue;
      out.push({ tool: lt.tool, resolver: l.resolver, launch: lt.path, user: ut.path, ...(lt.noExtension ? { launchNoExtension: true } : {}), ...(ut.noExtension ? { userNoExtension: true } : {}) });
    }
  }
  return out;
}
